import { type TFunction } from 'i18next';
import { ErrorCode, PERSONAL_SPACE_MAX_TASKS } from '@apm/contracts';
import { humanSize } from './bytes';
import { ApiRequestError } from './http';

/**
 * Đổi lỗi API thành câu hiển thị được.
 *
 * Tra theo `code`, **không bao giờ** theo `message` — `message` là văn bản và
 * văn bản thì đổi bất cứ lúc nào mà không ai coi đó là breaking change
 * (docs/api-conventions.md §3).
 *
 * Chưa có bản dịch cho mã nào đó thì dùng `message` từ server: nó đã là tiếng
 * Việt hiển thị được, tốt hơn nhiều so với một chuỗi "errors.SOMETHING" lọt ra
 * giao diện.
 */
export function errorMessage(error: unknown, t: TFunction): string {
  if (!(error instanceof ApiRequestError)) return t('errors.INTERNAL');

  const reason = error.details?.['reason'];
  // Tài khoản demo mở link mời vào tổ chức thật (APM-DEMO-inv) — mã chung FORBIDDEN không nói được vì sao.
  if (reason === 'ORG_INVITE_DEMO_ACCOUNT') return t('orgInvite.demoAccount');
  // Người ngoài vào tổ chức demo (APM-DEMO-out) — cũng là FORBIDDEN chung, không phải "thiếu quyền".
  if (reason === 'DEMO_ORG_NO_OUTSIDERS') return t('projectInviteCards.guest.demoOrg');
  // Link mời khách + đơn chờ duyệt — APM-CLINV-c.
  if (reason === 'CLIENT_INVITE_INVALID') return t('clientInvite.invalid');
  if (reason === 'CLIENT_REQUEST_DECIDED') return t('issueClients.requests.decided');
  if (reason === 'CLIENT_REQUEST_STAFF') return t('issueClients.requests.staff');
  // Cộng tác viên ngoài — APM-GUEST-c/e/f.
  if (reason === 'GUEST_INVITE_INVALID') return t('guestInvite.invalid');
  if (reason === 'GUEST_REQUEST_DECIDED') return t('projectGuests.requests.decided');
  if (reason === 'GUEST_REQUEST_STAFF') return t('projectGuests.errors.staff');
  if (reason === 'GUEST_CLIENT_ACCOUNT') return t('projectGuests.errors.clientAccount');
  if (reason === 'GUEST_ALREADY_MEMBER') return t('projectGuests.errors.alreadyMember');
  if (reason === 'GUEST_ROLE_TOO_WIDE') {
    const extra = error.details?.['permissions'];
    return t('projectGuests.errors.roleTooWide', {
      permissions: Array.isArray(extra) ? extra.join(', ') : '',
    });
  }
  // Loại issue — APM-ITYPE-b.
  if (typeof reason === 'string' && reason.startsWith('ISSUE_TYPE_')) {
    return t(`issueTypes.errors.${reason}`, { defaultValue: error.message });
  }
  if (reason === 'ORG_CREATE_DEMO_ACCOUNT') return t('org.demoAccount');
  // Đơn xin vào tổ chức — APM-ORGJR-b.
  if (reason === 'ORG_JOIN_REQUEST_DECIDED') return t('orgJoinRequests.decided');
  // Gỡ khỏi tổ chức — APM-ORGRM-a.
  if (reason === 'ORG_MEMBER_SELF') return t('memberRemoval.blocker.self');
  if (reason === 'ORG_MEMBER_SUPER_ADMIN') return t('memberRemoval.blocker.super_admin');
  if (reason === 'ORG_MEMBER_LAST_ADMIN') return t('memberRemoval.blocker.last_org_admin');
  if (reason === 'ORG_MEMBER_LAST_PM') {
    return t('memberRemoval.blocker.last_pm', { project: error.details?.['projectName'] });
  }
  if (reason === 'SPRINT_CLOSED') return t('board.sprintClosedGeneric');
  // Song ngữ — 404 nói tài liệu nào, vì sao, làm gì tiếp (APM-BIL-page), không câu chung NOT_FOUND.
  if (reason === 'TRANSLATION_DOC_STALE') return t('chilldocBilingual.errors.stale');
  if (reason === 'TRANSLATION_DOC_NOT_FOUND') return t('chilldocBilingual.errors.notFound');
  // Sổ chốt phương án — APM-DSHEET-c. Mã tới qua `code` thì nhánh `errors.${code}` cuối hàm lo.
  if (typeof reason === 'string' && reason.startsWith('DECISION_')) {
    return t(`errors.${reason}`, { defaultValue: error.message });
  }
  // Bấm giờ — APM-ACHEAT-d. `hours` cho trần ngày đi kèm trong `details`.
  if (typeof reason === 'string' && reason.startsWith('TIMESHEET_')) {
    return t(`timesheet.errors.${reason}`, { hours: error.details?.['hours'] ?? '' });
  }
  // Khoá lịch sử — APM-ACHEAT-a.
  if (reason === 'SPRINT_CLOSED_TASK') return t('board.sprintClosedTask');
  if (reason === 'TASK_COMPLETED_LOCKED') return t('board.taskCompletedLocked');
  if (reason === 'TASK_IN_BACKLOG') return t('board.progressInBacklog');
  if (reason === 'TASK_NOT_IN_PROGRESS') return t('board.progressNotInProgress');
  // Xoá / khôi phục việc — APM-TDEL-b.
  if (reason === 'TASK_DELETE_FORBIDDEN' || reason === 'TASK_RESTORE_PARENT_DELETED') {
    return t(`taskDelete.errors.${reason}`);
  }
  if (
    typeof reason === 'string' &&
    [
      'CHECKLIST_PROGRESS_LOCKED',
      'CHECKLIST_INCOMPLETE',
      'CHECKLIST_PARENT_CONFLICT',
      'CHECKLIST_STALE',
      'CHECKLIST_REOPEN_REQUIRED',
    ].includes(reason)
  )
    return t(`checklist.${reason}`);

  /*
    Cửa "bắt đầu làm" — APM-BOARD-g. Dịch kèm **tên việc đang chặn**: câu
    "việc phụ thuộc chưa xong" mà không nói việc nào thì người dùng phải tự đi
    dò trong Gantt.
  */
  if (typeof reason === 'string' && reason.startsWith('TASK_START_')) {
    return t(`taskStart.${reason}`, {
      blockedBy: error.details?.['blockedBy'] ?? '',
      blockedByNumber: error.details?.['blockedByNumber'] ?? '',
    });
  }

  /*
    Hạn mức dung lượng — APM-QUOTA-a. Câu phải có con số: "hết dung lượng" mà
    không nói đã dùng bao nhiêu trên bao nhiêu thì người dùng không biết cần
    xoá bớt bao nhiêu.
  */
  if (error.code === ErrorCode.STORAGE_QUOTA_EXCEEDED) {
    const size = (value: unknown): string => (typeof value === 'number' ? humanSize(value) : '—');
    return t('errors.STORAGE_QUOTA_EXCEEDED', {
      used: size(error.details?.['usedBytes']),
      quota: size(error.details?.['quotaBytes']),
      requested: size(error.details?.['requestedBytes']),
    });
  }

  /*
    Không gian cá nhân — APM-PSPACE-b. Câu theo **tính năng** bị chặn và chỉ lối ra (tham gia /
    lập tổ chức), không phải "không có quyền": người gọi là chủ, quyền thì đủ.
  */
  if (error.code === ErrorCode.PERSONAL_SPACE_RESTRICTED) {
    const feature = error.details?.['feature'];
    return typeof feature === 'string'
      ? t(`personalSpaceLimits.restricted.${feature}`, { defaultValue: error.message })
      : error.message;
  }
  if (error.code === ErrorCode.PERSONAL_SPACE_TASK_LIMIT) {
    return t('personalSpaceLimits.taskLimit', {
      limit: error.details?.['limit'] ?? PERSONAL_SPACE_MAX_TASKS,
      current: error.details?.['current'] ?? '—',
    });
  }

  /*
    Địa chỉ nội bộ bị chặn — APM-ORGINT-d/-e (plan-org-integrations §5). Người
    gặp câu này là admin tổ chức, còn người bật được công tắc là super admin, nên
    câu phải nói **nhờ ai** — và không kèm lỗi theo trường thô của API.
  */
  if (isPrivateNetworkBlocked(error)) return t('errors.PRIVATE_ADDRESS_BLOCKED');

  /*
    Nguồn đồng bộ — APM-SYNC-b. Xoá bị chặn thì câu phải có **số** dự án đang
    dùng; không tới được nguồn thì nói **vì sao** (`details.reason`): "sai địa
    chỉ" chung chung không phân biệt được trang HTML với một redirect sang host
    khác, và hai cái sửa theo hai cách khác nhau.
  */
  if (error.code === ErrorCode.SYNC_PROVIDER_IN_USE) {
    return t('errors.SYNC_PROVIDER_IN_USE', { n: error.details?.['connectionCount'] ?? '—' });
  }
  if (error.code === ErrorCode.SYNC_PROVIDER_UNREACHABLE) {
    const general = t('errors.SYNC_PROVIDER_UNREACHABLE');
    return typeof reason === 'string'
      ? t(`projectSync.unreachable.${reason}`, {
          defaultValue: general,
          host: error.details?.['redirectHost'] ?? '—',
          status: error.details?.['upstreamStatus'] ?? '—',
        })
      : general;
  }

  /*
    Máy chủ git — APM-GIT-b. Cùng hai luật với nguồn đồng bộ ngay trên: xoá bị
    chặn thì nói **còn gì** trỏ tới; không tới được thì nói **vì sao**.
  */
  /*
    Ghi ngược — APM-GIT-k. Token không ghi được thì câu phải nói **cần scope / vai
    gì** theo loại máy chủ (GitLab và GitHub gọi quyền ghi bằng hai bộ chữ khác
    nhau); câu chung "token không đủ quyền" không chỉ ra phải xin gì. Trùng tên
    nhánh thì nói **tên nào**.
  */
  if (error.code === ErrorCode.GIT_FORBIDDEN && error.details?.['feature'] === 'write') {
    const kind = error.details['hostKind'];
    const base =
      kind === 'gitlab' || kind === 'github'
        ? t(`gitTab.write.forbidden.${kind}`)
        : t('errors.GIT_FORBIDDEN');
    return reason === 'read_only' ? `${base} ${t('gitTab.write.forbiddenRotate')}` : base;
  }
  if (error.code === ErrorCode.GIT_BRANCH_EXISTS && typeof error.details?.['branch'] === 'string') {
    return t('gitTab.write.branchExists', { name: error.details['branch'] });
  }
  if (error.code === ErrorCode.GIT_HOST_IN_USE) {
    return t('errors.GIT_HOST_IN_USE', {
      connections: error.details?.['connectionCount'] ?? '—',
      identities: error.details?.['identityCount'] ?? '—',
    });
  }
  if (error.code === ErrorCode.GIT_HOST_UNREACHABLE) {
    const general = t('errors.GIT_HOST_UNREACHABLE');
    return typeof reason === 'string'
      ? t(`gitTab.hosts.unreachable.${reason}`, {
          defaultValue: general,
          host: error.details?.['redirectHost'] ?? '—',
          status: error.details?.['upstreamStatus'] ?? '—',
        })
      : general;
  }

  // Bị khoá ở mọi tổ chức — APM-MSUS-b. Câu phải nói khoá ở **đâu** để biết hỏi ai.
  if (error.code === ErrorCode.MEMBERSHIP_SUSPENDED) {
    const names = error.details?.['orgNames'];
    return t('errors.MEMBERSHIP_SUSPENDED', {
      orgs: Array.isArray(names) ? names.join(', ') : '—',
    });
  }

  const key = `errors.${error.code}`;
  const translated = t(key);
  const base = translated === key ? error.message : translated;

  const fields = fieldIssues(error.details);
  if (fields.length > 0) return `${base} ${fields.join(' ')}`;

  /**
   * `VALIDATION_FAILED` là mã **ô dù**, và chỉ riêng nó cần thêm `message`.
   *
   * Cùng một mã phủ hai thứ khác hẳn nhau: lỗi Zod theo trường (có `details`,
   * đã xử ở trên) và lỗi luật nghiệp vụ chỉ có một câu — ví dụ
   * `AppError.validationFailed('Cột trong sprint và cột ngoài sprint sắp xếp
   * riêng')`. Với vế thứ hai, bản dịch chung "Dữ liệu chưa hợp lệ" không nói
   * được gì, và người dùng sẽ thử lại đúng cái vừa sai. Đã tốn của một người
   * dùng thật một vòng như vậy với nút chuyển cột board.
   *
   * Các mã khác thì bản dịch đã là câu hoàn chỉnh, nên `message` thô của server
   * không được lọt ra — đó là điều `errorMessage` được viết ra để bảo đảm, và
   * có test riêng canh.
   */
  if (error.code !== ErrorCode.VALIDATION_FAILED) return base;

  const specific = error.message.trim();
  return specific === '' || specific === base ? base : `${base} ${specific}`;
}

/**
 * Câu 403 **chung** ("Bạn không có quyền thực hiện thao tác này.") — người dùng
 * yêu cầu 2026-09-30: chỗ nào lẽ ra hiện câu này thì **ẩn hẳn**, coi như người
 * xem không thấy khối ấy, không kèm thông báo gì.
 *
 * So trên **câu đã dịch** chứ không trên `code`: đó đúng là tiêu chí người dùng
 * đặt, và nó để yên những 403 có lý do riêng (`TASK_DELETE_FORBIDDEN`, tài
 * khoản demo mở link mời…) — những câu ấy nói được vì sao và bước tiếp theo,
 * nên chúng vẫn hiện.
 *
 * `ErrorState` và `ChartFrame` đã tự gọi hàm này; chỗ nào tự in lỗi ra JSX thì
 * dùng `isForbidden(error, t)`.
 */
export function isForbiddenMessage(message: string | null | undefined, t: TFunction): boolean {
  return typeof message === 'string' && message === t('errors.FORBIDDEN');
}

/** `isForbiddenMessage` cho một lỗi chưa dịch (lỗi của query / mutation). */
export function isForbidden(error: unknown, t: TFunction): boolean {
  return error != null && isForbiddenMessage(errorMessage(error, t), t);
}

/**
 * Lỗi validate **theo từng trường**, ghép vào sau câu chung.
 *
 * ## Vì sao cần
 *
 * `ZodValidationPipe` trả `details` dạng `{ baseUrl: ['Invalid URL'] }`, nhưng
 * bản dịch của `VALIDATION_FAILED` chỉ nói "Dữ liệu chưa hợp lệ. Vui lòng kiểm
 * tra lại." Người dùng nhìn thấy một toast đỏ mà **không biết sai chỗ nào**, và
 * họ sẽ thử lại đúng cái vừa sai.
 *
 * Vẫn giữ câu chung ở đầu: nó là câu đã dịch, còn phần chi tiết tới từ Zod nên
 * có thể là tiếng Anh với những schema chưa đặt `message` — tốt hơn nhiều so
 * với không nói gì, nhưng không thay được câu chính.
 *
 * Chỉ nhận `Record<string, string[]>`; hình dạng khác thì bỏ qua chứ không đoán:
 * `details` cũng được dùng cho những thứ không phải lỗi trường (`upstream`,
 * `spent`/`limit` của hạn mức AI), và in chúng ra sẽ thành câu vô nghĩa.
 */
function fieldIssues(details: Record<string, unknown> | undefined): string[] {
  if (!details) return [];

  return Object.entries(details)
    .filter(
      (entry): entry is [string, string[]] =>
        Array.isArray(entry[1]) && entry[1].every((item) => typeof item === 'string'),
    )
    .map(([field, messages]) => `${field}: ${messages.join(', ')}.`);
}

/**
 * Lỗi "địa chỉ nội bộ bị chặn" của APM-ORGINT-d (400 `PRIVATE_ADDRESS_BLOCKED`):
 * lúc lưu, `details` theo trường (`{ apiBaseUrl: [...] }`); lúc gọi (Kiểm tra,
 * lượt đồng bộ) `details.host`.
 */
export function isPrivateNetworkBlocked(error: unknown): boolean {
  return error instanceof ApiRequestError && error.code === ErrorCode.PRIVATE_ADDRESS_BLOCKED;
}

/** Ô nào của form bị chặn (`details` theo trường của lúc lưu); không rõ thì `null`. */
export function privateNetworkField<F extends string>(
  error: unknown,
  fields: readonly F[],
): F | null {
  if (!(error instanceof ApiRequestError) || !isPrivateNetworkBlocked(error)) return null;
  return fields.find((field) => error.details?.[field] !== undefined) ?? null;
}
